import { useRef } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router'

import { useCatalogFilterHydration } from 'features/catalog/hooks/use-catalog-filter-hash/use-catalog-filter-hydration'
import { useCatalogFilterSeam } from 'features/catalog/hooks/use-catalog-filter-hash/use-catalog-filter-seam'
import { useCatalogFilterWriter } from 'features/catalog/hooks/use-catalog-filter-hash/use-catalog-filter-writer'
import {
  type CatalogYogiQLPage,
  YOGIQL_PAGE_OFF,
} from 'features/catalog/hooks/use-catalog-yogiql-page'
import type { CatalogService } from 'features/catalog/service'
import { useProjectStore } from 'features/project/store'
import type { ProjectState } from 'types/project'

const FILTERS_PARAM = 'filters'

// ---------------------------------------------------------------------------
// Owns catalog's filter <-> URL lifecycle through a `?filters=<hash>` query
// param — a query param, NOT a path segment, because catalog's path segments
// are entity navigation (`/catalog/brands/:brandName`, ...) and catalog is
// deliberately NOT in `routes/builders.ts`'s `SCREEN_STATE_PAGES` (adding it
// there would make `usePageFilterHash`'s legacy-query redirect rewrite
// `?filters=` into a path segment, colliding with the entity routes).
//
// Orchestrator over two private sub-hooks. Every hub reads keys through
// `/client/catalog/yogiql/page-state`; off `enhanced_dialect` writes go to
// `/project/hash-filter`, under it to the YogiQL route (`useCatalogFilterSeam`):
//
//   `useCatalogFilterHydration` (URL -> state): on mount, if `?filters=` is
//   present, loads the saved panel from the backend. Otherwise, on a PUSH
//   navigation (the user clicked into Catalog from another page), overlays
//   the global v1 filter store's current filters — a mount-only pass, so it
//   can never re-fire later. A cold load/refresh (POP) with no hash just
//   keeps the default panel. A later change to `?filters=` that this hook
//   didn't itself write re-hydrates from the new hash (e.g. back/forward
//   while Catalog stays mounted), or, if the hash disappeared instead (e.g.
//   the "Catalog" breadcrumb, which links to the bare path with no
//   `?filters=`), clears the panel to match rather than leaving it stale.
//
//   `useCatalogFilterWriter` (state -> URL): once `settled` (hydration has
//   caught up with whatever `?filters=` currently says), every panel change
//   is saved and its hash written into `?filters=` (replace, so editing
//   filters doesn't pollute back/forward history one entry per change). An
//   empty panel removes the param instead of saving an empty filter set.
//
// `catalogService` is injected by the caller (Container.tsx) rather than
// resolved via `useCatalogService()` in here — service hooks are only called
// from `Container.tsx` per `client/AGENTS.md`'s architecture.
//
// The outbound catalog -> global-store leg is the already-generic
// `usePublishPanelsToGlobalFilters`, wired up from Container.tsx.
// ---------------------------------------------------------------------------

/** Returns whether the panel currently reflects the URL (hydration has caught
 *  up with `?filters=`) — callers that must not observe the pre-hydration
 *  default panel gate on it. */
export function useCatalogFilterHash(
  catalogService: CatalogService,
  yogiql: CatalogYogiQLPage = YOGIQL_PAGE_OFF,
): boolean {
  const location = useLocation()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const projectId = useProjectStore((state: ProjectState) => state.projectId)

  // Under `enhanced_dialect` the state and its key are YogiQL; see the seam.
  const seam = useCatalogFilterSeam(catalogService, yogiql)

  const hashFromUrl = new URLSearchParams(location.search).get(FILTERS_PARAM)
  const lastWrittenHashRef = useRef<string | null>(null)
  const needsFetch = !!hashFromUrl && hashFromUrl !== lastWrittenHashRef.current

  const hydrated = useCatalogFilterHydration({
    hashFromUrl,
    needsFetch,
    navigationType,
    projectId,
    seam,
    lastWrittenHashRef,
  })

  const settled = hydrated && !needsFetch

  useCatalogFilterWriter({
    // Distinct from `hydrated`: `hydrated` latches true after the FIRST
    // hydration pass and stays true. `settled` drops back to false whenever
    // a newer `?filters=` hash appears that hasn't been loaded yet
    // (`needsFetch` flips true again on a back/forward navigation). The
    // writer must gate on `settled`, not `hydrated` — otherwise, on the
    // render where a new hash has just appeared in the URL but the panel is
    // still whatever was there before (typically empty), its "no filters ->
    // delete the param" branch would race the in-flight load and strip the
    // incoming hash before it's ever read.
    settled,
    projectId,
    seam,
    location,
    navigate,
    lastWrittenHashRef,
  })

  return settled
}
